/**
 * Data the page already has before any fetch: the catalogue, the worlds and
 * a book's reviews, handed over by the server that rendered the page (issue
 * #49, clause 2).
 *
 * The Worker renders a world's pages to HTML, so the reader sees the content
 * in the first response instead of after the bundle and an API call. It puts
 * the data it rendered from into the page as a JSON block; the browser's
 * first render is built from the same data and needs no request.
 *
 * ## Through React, not through this module (#95)
 *
 * The seed reaches components through `SeedContext`, provided by the render
 * itself: `renderPage` on the server, `main.tsx` in the browser. A component
 * reads it with the hooks below, and on the server that is the only way it is
 * read. It used to be a module-level variable set before `renderToString` and
 * cleared after, which was safe only while rendering was synchronous: the day
 * a streaming render interleaved two requests in one isolate, a page could be
 * built from another request's seed. Through context, each render carries its
 * own, whatever the render model. `tests/unit/seed-context.test.ts` renders two
 * worlds interleaved to prove it.
 *
 * The browser also keeps a module copy (`setBrowserSeed`), for the fetches
 * that run in effects and want to skip a request the seed already answered.
 * A browser has one reader, so that is not shared state, and the setter
 * refuses to run anywhere but a browser.
 */

import { createContext, createElement, useContext, type ReactNode } from 'react'
import type { CatalogueResponse, WorldsResponse } from '../../shared/catalogue.ts'
import type { PublicReview } from '../../shared/reviews.ts'

export interface PageSeed {
  /** The world the page is in, and its catalogue. */
  world?: { slug: string; catalogue: CatalogueResponse }
  worlds?: WorldsResponse
  /** On a book's page: its visible reviews, rendered with it (issue #89). */
  reviews?: { world: string; work: string; items: PublicReview[] }
}

const SeedContext = createContext<PageSeed>({})

/** Gives a render its seed: one per server render, one per page load in the browser. */
export function SeedProvider({ seed, children }: { seed: PageSeed; children?: ReactNode }) {
  return createElement(SeedContext.Provider, { value: seed }, children)
}

export function catalogueIn(seed: PageSeed, world: string): CatalogueResponse | null {
  return seed.world?.slug === world ? seed.world.catalogue : null
}

export function worldsIn(seed: PageSeed): WorldsResponse | null {
  return seed.worlds ?? null
}

export function reviewsIn(seed: PageSeed, world: string, work: string): PublicReview[] | null {
  const reviews = seed.reviews
  return reviews !== undefined && reviews.world === world && reviews.work === work ? reviews.items : null
}

/** The seeded catalogue of `world`, from this render's seed. */
export function useSeededCatalogue(world: string): CatalogueResponse | null {
  return catalogueIn(useContext(SeedContext), world)
}

export function useSeededWorlds(): WorldsResponse | null {
  return worldsIn(useContext(SeedContext))
}

export function useSeededReviews(world: string, work: string): PublicReview[] | null {
  return reviewsIn(useContext(SeedContext), world, work)
}

let browserSeed: PageSeed = {}

/** The browser's copy, set once by main.tsx. Never on the server. */
export function setBrowserSeed(seed: PageSeed): void {
  if (!('document' in globalThis)) throw new Error('setBrowserSeed is for the browser; a server render passes its seed through SeedProvider')
  browserSeed = seed
}

/** For effect-time fetches in the browser only: the seed this page loaded with. */
export function browserSeedNow(): PageSeed {
  return browserSeed
}

/** The id of the JSON block the Worker writes and main.tsx reads. */
export const SEED_ELEMENT_ID = 'page-seed'
